import { useCallback, useMemo, useRef, useState } from "react";
import { Badge, Button, Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import DateTimeInput from "../common/components/DateTimeInput";
import DefaultButton from "../common/components/DefaultButton";
import { Col, Row } from "../common/components/Grid";
import Icon from "../common/components/Icon";
import ReactForm from "../common/components/ReactForm";
import { useResizeContext } from "../common/components/ResizeablePanel";
import TextInput from "../common/components/TextInput";
import VerticalCarousel from "../common/components/VerticalCarousel";
import useCredentials from "../common/hooks/credentialsHook";
import useNetwork from "../common/hooks/networkHook";
import { ReactNodeDialog } from "../common/utils/dialogUtlities";
import { dynamicSort } from "../common/utils/tableUtilities";
import { addAlert, canCreateAlert, canRemoveAlert, flagAlert, markAlertSeen, removeAlert, unflagAlert, updateAlert, useAlerts } from "../reducers/alertSlice";
import { useCADPopout } from "../utils/CADStateProvider";
import PanelTitle from "../common/components/PanelTitle";

const AlertDetailsDialog = ({ alert, onClose }) => {
    const { t } = useTranslation();
    const network = useNetwork();
    const [title, setTitle] = useState(alert?.title);
    const [message, setMessage] = useState(alert?.message);
    const validationRef = useRef();

    const handleSave = useCallback(() => {
        if (validationRef.current?.checkValidity()) {
            if (alert?.alertid) {
                updateAlert(network, { alertid: alert.alertid, title, message });
            } else {
                addAlert(network, { title, message });
            }
            onClose();
        }
    }, [alert, network, title, message, onClose]);

    return <ReactNodeDialog title={alert ? t('newAlert') : t('alert', { count: 1 })} showModal={!!alert} onSave={handleSave} onClose={onClose}>
        <ReactForm ref={validationRef}>
            <Row>
                <Col>
                    <TextInput placeholder={t('title')} value={title} live onChange={setTitle} required></TextInput>
                </Col>
            </Row>
            <Row>
                <Col>
                    <TextInput placeholder={t('message')} value={message} live onChange={setMessage} required></TextInput>
                </Col>
            </Row>
        </ReactForm>
    </ReactNodeDialog>
}

const AlertEntry = ({ alert, showCarousel }) => {
    const { t } = useTranslation();
    const network = useNetwork();
    const credentials = useCredentials();
    const { height } = useResizeContext();

    const processAlert = useCallback((e, alert) => {
        e.preventDefault(); e.stopPropagation();
        if (!alert.seen) markAlertSeen(network, alert);
        else if (alert.flagged) unflagAlert(network, alert);
        else flagAlert(network, alert);
    }, [network]);

    return <div key={alert.alertid} className={`d-flex flex-column w-100 h-100 ${height > 100 && 'p-2'} ${!showCarousel && 'mb-2'} ${!alert.seen ? 'alert-danger' : alert.flagged ? 'bg-warning' : 'bg-light'}`}>
        <div className={`flex-grow-1 d-flex flex-column`}>
            <Row>
                <Col className="d-flex flex-column" xs="auto">
                    <Button className="py-0 px-1 mx-1" size="sm" variant="outline-dark" style={{ border: 'none' }} onClick={e => processAlert(e, alert)}>
                        {!alert.seen && <Icon variant="solid" kind="eye" />}
                        {alert.seen && alert.flagged && <Icon variant="solid" kind="star" />}
                        {alert.seen && !alert.flagged && <Icon variant="regular" kind="star" />}
                    </Button>
                    {canRemoveAlert(credentials, alert) && height < 100 && (
                        <DefaultButton confirmMessage={t('confirmDeleteAlert', { title: alert.title, id: alert.alertid })} variant="outline-dark" hideLabel size="xs" style={{border: 0}} kind="delete" onClick={() => removeAlert(network, alert)}/>
                    )}
                </Col>
                <Col className={`d-flex ${height < 100 ? 'flex-row align-items-end' : 'flex-column justify-content-center'}`}>
                    <Row>
                        <Col>
                            <Row><Col style={{ fontWeight: 'bold', fontSize: showCarousel ? '12px' : undefined }}>{alert.title}</Col></Row>
                            <Row><Col>{alert.message}</Col></Row>
                            <Row className="text-start" style={{ fontSize: '10px', color: 'grey' }}>
                                <Col><DateTimeInput value={alert.created} hideLabel readonly /></Col>
                            </Row>
                        </Col>
                    </Row>
                    <Row>
                        
                    </Row>
                </Col>
                <Col xs="auto" className="d-flex align-items-start">
                    {canRemoveAlert(credentials, alert) && height > 100 && (
                        <DefaultButton confirmMessage={t('confirmDeleteAlert', { title: alert.title, id: alert.alertid })} variant="outline-dark" hideLabel size="sm" kind="delete" onClick={() => removeAlert(network, alert)}/>
                    )}
                </Col>
            </Row>
        </div>
    </div>
}

const AlertList = ({ alerts, showCarousel }) => {
    const { t } = useTranslation();

    const sortedAlerts = useMemo(() => [...alerts].sort(dynamicSort(['-flagged', 'seen', '-created'])), [alerts]);

    if (!sortedAlerts.length) {
        return <div className="p-2">{t('noAlerts')}</div>;
    }

    if (showCarousel) {
        return <div className="d-flex flex-column w-100 h-100 position-relative" style={{ overflowY: 'hidden', padding: 0, margin: 0 }}>
            <VerticalCarousel items={sortedAlerts.map(alert => <AlertEntry key={alert.id} alert={alert} showCarousel={showCarousel}></AlertEntry>)} interval={5000} showControls showPause />
        </div>
    }

    return <div className="d-flex flex-column" style={{ overflowY: 'auto' }}>
        {sortedAlerts.map(alert => <AlertEntry key={alert.id} alert={alert} showCarousel={showCarousel}></AlertEntry>)}
    </div>
};

export const AlertNav = () => {
    const [editAlert, setEditAlert] = useState();
    const credentials = useCredentials();
    const alerts = useAlerts();
    const unread = useMemo(() => alerts.filter(a => !a.seen || a.flagged).length, [alerts]);
    const canCreate = useMemo(() => canCreateAlert(credentials), [credentials]);

    return <>
        {editAlert && <AlertDetailsDialog alert={editAlert} onClose={() => setEditAlert()} /> }
        <Dropdown style={{ height: '100%' }}>
            <Dropdown.Toggle className="px-1" as={Button} variant={unread > 0 ? 'danger' : null}
                style={{ color: 'white', backgroundColor: unread === 0 ? 'transparent' : undefined, height: '100%' }}>
                <Icon kind="bell" variant="solid" /><Badge>{unread}</Badge>
            </Dropdown.Toggle>
            <Dropdown.Menu className="p-0" style={{ minWidth: 'min(500px, max(33vw, 300px))' }} alignRight>
                {canCreate && (
                    <Dropdown.Item style={{ position: 'sticky', top: 0, zIndex: 5, backgroundColor: 'white' }}>
                        <DefaultButton className="w-100" kind="add" onClick={() => setEditAlert({})} />
                    </Dropdown.Item>
                )}
                <AlertList alerts={alerts} onEditAlert={setEditAlert} />
            </Dropdown.Menu>
        </Dropdown>
    </>;
};

export const AlertPanel = () => {
    const { t } = useTranslation();
    const credentials = useCredentials();
    const alerts = useAlerts();
    const [editAlert, setEditAlert] = useState();
    const popout = useCADPopout();
    const { height } = useResizeContext();
    const showCarousel = height !== undefined && height < 300;
    const canCreate = useMemo(() => canCreateAlert(credentials), [credentials])

    return <div className="w-100 h-100 d-flex flex-column">
        <PanelTitle title={t('alert_other')} onPopout={() => popout('alertInfoPanel')}>
            <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'end', rowGap: '10px' }}>
                {canCreate && <div className="mb-2">
                    <DefaultButton kind="add" onClick={() => setEditAlert({})}>
                        {t('addAlert')}
                    </DefaultButton>
                </div>}
            </div>
        </PanelTitle>
        {editAlert && <AlertDetailsDialog alert={editAlert} onClose={() => setEditAlert()} />}
        <div className={`d-flex flex-column ${showCarousel ? 'flex-grow-1' : ''}`} style={{ overflow: showCarousel ? 'hidden' : 'auto', minHeight: 0 }}>
            <AlertList alerts={alerts} showCarousel={showCarousel} onEditAlert={setEditAlert} />
        </div>
    </div>
};